import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  useTheme,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  CurrencyExchange as CurrencyExchangeIcon,
} from "@mui/icons-material";

const ExchangeRateDisplay = ({
  exchangeRate,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Exchange Rates
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Loading exchange rates...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            Exchange Rates Unavailable
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!exchangeRate) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Exchange Rates
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No exchange rate data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <CurrencyExchangeIcon
            sx={{ mr: 1, color: theme.palette.primary.main }}
          />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Current Exchange Rates
          </Typography>
          <Chip
            size="small"
            label={exchangeRate.validflag ? "Active" : "Inactive"}
            color={exchangeRate.validflag ? "success" : "error"}
          />
        </Box>

        <Grid container spacing={2}>
          {/* USD to THB */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoneyIcon sx={{ mr: 1, color: "#2e7d32" }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  USD → THB
                </Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {exchangeRate.USD ? exchangeRate.USD.toFixed(2) : "N/A"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                1 USD = {exchangeRate.USD ? exchangeRate.USD.toFixed(2) : "N/A"}{" "}
                THB
              </Typography>
            </Box>
          </Grid>

          {/* LAK to THB */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUpIcon sx={{ mr: 1, color: "#1976d2" }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  LAK → THB
                </Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {exchangeRate.LAK ? exchangeRate.LAK.toFixed(2) : "N/A"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {exchangeRate.LAK
                  ? exchangeRate.LAK.toFixed(2) + " LAK"
                  : "N/A"}{" "}
                = 1 THB
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Additional Information */}
        <Box mt={2} pt={2} borderTop={1} borderColor="divider">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Last Updated:</strong>{" "}
                {exchangeRate.updatedAt
                  ? formatDate(exchangeRate.updatedAt)
                  : "N/A"}
              </Typography>
            </Grid>
            {exchangeRate.remark && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Note:</strong> {exchangeRate.remark}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Conversion Examples */}
        <Box
          mt={2}
          p={2}
          sx={{ backgroundColor: theme.palette.grey[50], borderRadius: 1 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Quick Conversion Examples:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                $100 USD = ₿
                {exchangeRate.USD
                  ? (100 * exchangeRate.USD).toLocaleString()
                  : "N/A"}{" "}
                THB
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                ₿100 THB = ₭
                {exchangeRate.LAK
                  ? (100 * exchangeRate.LAK).toLocaleString()
                  : "N/A"}{" "}
                LAK
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                ₿1,000 THB = ₭
                {exchangeRate.LAK
                  ? (1000 * exchangeRate.LAK).toLocaleString()
                  : "N/A"}{" "}
                LAK
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExchangeRateDisplay;
